/**
 * A lazily loaded set of content modules (the phrases course's lessons —
 * lessons.ts). Pure: no React, no Vite, so the loading rules are unit-tested
 * with fake loaders (lazyContent.test.ts).
 *
 *   - `load()` is idempotent: every caller shares ONE in-flight promise, and
 *     once the content is in, it resolves at once without touching the
 *     loaders again.
 *   - All or nothing: `commit` receives every module at once and either
 *     indexes all of them or throws — so a retry never trips over half a
 *     batch it added the first time.
 *   - A failure (offline chunk fetch, invalid JSON) leaves status 'error' and
 *     rejects; the next `load()` starts a fresh attempt.
 *   - `subscribe` is the React bridge (useSyncExternalStore): listeners hear
 *     every status change.
 */

export type LazyContentStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface LazyContent {
  /** Start the load, or join the one in flight. Resolves once the content is
   *  indexed; rejects on failure (a later call tries again). */
  load: () => Promise<void>;
  status: () => LazyContentStatus;
  /** Returns the unsubscribe function. */
  subscribe: (listener: () => void) => () => void;
}

export type ContentLoaders = Readonly<Record<string, () => Promise<unknown>>>;

export function createLazyContent(
  loaders: ContentLoaders,
  commit: (entries: ReadonlyArray<readonly [path: string, raw: unknown]>) => void,
): LazyContent {
  let status: LazyContentStatus = 'idle';
  let inFlight: Promise<void> | null = null;
  const listeners = new Set<() => void>();

  const setStatus = (next: LazyContentStatus) => {
    if (status === next) return;
    status = next;
    for (const listener of [...listeners]) listener();
  };

  const load = (): Promise<void> => {
    if (status === 'ready') return Promise.resolve();
    if (inFlight) return inFlight;
    const attempt = Promise.all(
      Object.entries(loaders).map(async ([path, loader]) => [path, await loader()] as const),
    )
      .then((entries) => {
        commit(entries);
        inFlight = null;
        setStatus('ready');
      })
      // A loader failed (offline, a chunk gone after a deploy) or `commit`
      // threw (invalid or duplicate lesson): nothing was indexed.
      .catch((error: unknown) => {
        inFlight = null;
        setStatus('error');
        throw error;
      });
    // Shared BEFORE anyone hears 'loading', so a listener that calls load()
    // joins this attempt instead of starting a second one.
    inFlight = attempt;
    setStatus('loading');
    return attempt;
  };

  return {
    load,
    status: () => status,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
